import { MagnifyingGlassIcon } from '@phosphor-icons/react'
import { useLocation } from 'react-router-dom'
import { Undo, LogOut } from 'iconoir-react'
import { startCase } from 'lodash'
import { observer } from 'mobx-react-lite'
import { Fragment, ReactElement, ReactNode, useCallback, useEffect, useState } from 'react'
import NotificationPanel from '../notifications/NotificationPanel'
import { Tooltip } from '../Tooltip'
import { UpgradeBanner } from './UpgradeBanner'
import { Feature } from 'app/components/auth/Feature'
import { openGlobalSearch } from 'app/components/global-search/globalSearchBus'
import { SHORTCUT_LABEL } from 'app/components/global-search/shortcut'
import NameIcon from 'app/components/shared/NameIcon'
import { GlobalWorkspaceSelectorV2 } from 'app/components/sidebar/GlobalWorkspaceSelectorV2'
import { IntercomHelpButton } from 'app/components/sidebar/IntercomHelpButton'
import { UserMenu } from 'app/components/sidebar/UserMenu'
import { ThemeSwitch } from 'app/components/sidebar/ThemeSwitch'
import { intercomEnabled } from 'app/intercom'
import { useSubnavContext } from 'app/components/SideBarContext'
import { Button } from 'app/components/ui/button'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useIsOrgUser } from 'app/hooks/useIsOrgUser'
import { getLegacyRoleName } from 'app/types/roles'
import { useUser, useWorkspace } from 'app/utils'
import type { NavItemType } from 'types/Sidebar'
import { useAuthStore } from 'app/stores/StoreContext'
import { titleCase } from 'app/utils/titleCase'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import { lastNameInitial } from 'lib/nameUtils'
import { gqlFetchOne } from 'app/lib/graphqlClient'

export const SidebarChrome = observer(function SidebarChrome({
  children,
  userMenuItems,
}: {
  children: ReactNode
  userMenuItems?: NavItemType[]
}): ReactElement {
  const user = useUser()
  const auth = useAuthStore()
  const { products } = auth
  const workspace = useWorkspace()
  const isOrgUser = useIsOrgUser()
  const staffV2Enabled = useFeatureToggle(`staff-v2`)
  const globalSearchEnabled = useFeatureToggle(`global-search`)
  const location = useLocation()
  const subnav = useSubnavContext()
  const [roleName, setRoleName] = useState<string>(``)

  useEffect(() => {
    if (!staffV2Enabled || !user?.roleId) return

    const fetchRoleName = async () => {
      try {
        const role = await gqlFetchOne<{ id: string; name: string }>('role', user.roleId!, 'id name')
        if (role?.name) {
          setRoleName(role.name)
        }
      } catch (err: unknown) {
        logError(`Error fetching role name`, { error: err })
      }
    }
    bg(fetchRoleName)
  }, [staffV2Enabled, user?.roleId])

  const getRoleName = useCallback(() => {
    return titleCase(startCase(staffV2Enabled ? roleName : getLegacyRoleName(user?.role ?? 0)))
  }, [staffV2Enabled, user, roleName])

  let userInitials = `${user?.name?.[0]}${lastNameInitial(user?.name ?? ` `)?.toLocaleUpperCase()}`
  userInitials = userInitials?.replace(/undefined/gi, ``)

  // The notifications slot only renders for workspace (non-org) users. For org
  // users it must NOT render an empty padded div, or the dead space shrinks the
  // scrollable nav list and clips its last item on short screens.
  const showNotifications = workspace != null && !isOrgUser
  const helpEnabled = intercomEnabled()

  if (!user) return <></>

  return (
    <aside id="sidebar-v2" aria-label="Sidebar" className="flex h-full md:h-screen">
      <nav id="navs" className="flex w-70 flex-col divide-y divide-subtle border-r border-default bg-surface-subtle">
        <div className="shrink-0">
          <GlobalWorkspaceSelectorV2 />
        </div>
        <div className="flex grow flex-col overflow-hidden py-4 pl-2 pr-3">
          {/* Inside the nav column, not a sibling of it, so the nav's divide-y draws no
              hairline between the search row and the first item. */}
          {globalSearchEnabled && (
            <div className="shrink-0 pb-3 pl-1">
              <Button
                variant="field"
                size="md"
                onClick={openGlobalSearch}
                aria-label="Search"
                aria-keyshortcuts="Control+K Meta+K"
                className="w-full"
              >
                <MagnifyingGlassIcon size={16} weight="bold" className="text-muted-foreground" />
                <span className="grow text-left text-muted-foreground">Search</span>
                {/* oxlint-disable-next-line shadcn/no-arbitrary-values -- the shortcut hint sits between the 10px and 12px steps */}
                <kbd className="rounded border border-default bg-surface-subtle px-1.5 py-0.5 font-sans text-[11px] text-subtle">
                  {SHORTCUT_LABEL}
                </kbd>
              </Button>
            </div>
          )}
          <div className="sidebar-scrollbar flex min-h-0 grow flex-col overflow-y-auto">{children}</div>
          {(showNotifications || helpEnabled) && (
            <div className="flex shrink-0 cursor-pointer flex-col pt-3">
              {showNotifications && (
                <Feature name="notifications">
                  <NotificationPanel />
                </Feature>
              )}
              <IntercomHelpButton />
            </div>
          )}
        </div>
        {products?.includes('AI Agent') && !products?.includes('CRM') && <UpgradeBanner />}
        {userMenuItems ? (
          <div className="w-full shrink-0 p-2">
            <UserMenu user={user} items={userMenuItems} initials={userInitials} roleName={getRoleName()} />
          </div>
        ) : (
          <div className="w-full shrink-0 py-4 pl-4 pr-5">
            <div className="flex items-center justify-between gap-x-2">
              <div className="pointer-events-none flex min-w-0 grow items-center gap-x-2">
                <NameIcon
                  fullName={userInitials}
                  heightClassName="h-8"
                  widthClassName="w-8"
                  backgroundClassName="bg-fill-neutral-strong"
                  textClassName="text-ui-sm leading-tight text-on-color"
                  isTiny={true}
                />
                <div className="flex w-full grow flex-col justify-between">
                  <div className="text-left text-ui-sm text-strong">{user?.name}</div>
                  <div className="text-left text-label-xs capitalize text-subtle">{getRoleName()}</div>
                </div>
              </div>
              {document.location.origin === 'https://next.app.trackable.io' && (
                <Tooltip message="Back to legacy" className="!cursor-pointer">
                  <Button
                    variant="subtle"
                    size="icon-sm"
                    aria-label="Back to legacy"
                    onClick={() => {
                      document.cookie =
                        'trackable-classic=1;max-age=43200;domain=.trackable.io;path=/;secure;samesite=lax'
                      window.location.href = 'https://app.trackable.io'
                    }}
                  >
                    <Undo className="h-5 w-5 text-strong" />
                  </Button>
                </Tooltip>
              )}
              <ThemeSwitch />
              <Tooltip message="Log out" className="!cursor-pointer">
                <Button
                  variant="subtle"
                  size="icon-sm"
                  aria-label="Log out"
                  onClick={async () => {
                    await auth.logout()
                    // Hard redirect to a clean /login. A client-side navigate() races
                    // with AppLayout's auth-redirect effect (which fires
                    // window.location.href to /login?redirectTo=<authed path> once
                    // user becomes undefined), leaving the user on a bounced URL.
                    window.location.href = '/login'
                  }}
                >
                  <LogOut className="h-5 w-5 text-strong" />
                </Button>
              </Tooltip>
            </div>
          </div>
        )}
      </nav>
      {location.pathname.match(/^\/inbox\/?/) && subnav?.length > 0 && (
        <div
          id="inbox-secondary-nav"
          className="mb-3 flex h-full min-h-0 flex-col overflow-hidden border-r border-r-default bg-surface pb-6 transition-all"
        >
          {subnav.map((item, i) => (
            <Fragment key={i}>
              {item.children?.map((child, i2) => (
                <Fragment key={`${i}-${i2}`}>
                  {child.customComponent && child.customComponent}
                  {child.filter && child.filter}
                </Fragment>
              ))}
            </Fragment>
          ))}
        </div>
      )}
    </aside>
  )
})
